import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Button,
} from '@tonic/shared/components/ui';
import { Plus, ArrowUpRight, ArrowDownRight, Leaf } from 'lucide-react';
import { DashboardChart } from '@tonic/shared/components/ui';

// Mock data for recycling overview
const mockData = {
  metrics: {
    totalRecycled: { value: 1234, change: 12.3, trend: 'up' },
    materialsRecovered: { value: 856, change: 8.7, trend: 'up' },
    carbonOffset: { value: 2.4, change: 15.2, trend: 'up' },
    activePrograms: { value: 8, change: 2, trend: 'up' }
  },
  revenueChart: [
    { date: '2024-01', value: 12500 },
    { date: '2024-02', value: 15800 },
    { date: '2024-03', value: 18200 },
  ],
  materialChart: [
    { material: 'Wood', value: 450 },
    { material: 'Metal', value: 320 },
    { material: 'Plastic', value: 280 },
    { material: 'Electronics', value: 180 },
  ]
};

export function RecycleOverview() {
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Recycling Overview</h1>
          <p className="text-sm text-muted-foreground">
            Track recycling performance and environmental impact
          </p>
        </div>
        <Button>
          <Plus className="w-4 h-4 mr-2" />
          New Recycling Request
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Items Recycled</CardTitle>
            <Leaf className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{mockData.metrics.totalRecycled.value}</div>
            <div className="flex items-center space-x-2">
              <Badge variant={mockData.metrics.totalRecycled.trend === 'up' ? 'default' : 'destructive'}>
                {mockData.metrics.totalRecycled.trend === 'up' ? (
                  <ArrowUpRight className="w-4 h-4" />
                ) : (
                  <ArrowDownRight className="w-4 h-4" />
                )}
                {mockData.metrics.totalRecycled.change}%
              </Badge>
              <span className="text-sm text-muted-foreground">vs. last period</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Materials Recovered (kg)</CardTitle>
            <Leaf className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{mockData.metrics.materialsRecovered.value}</div>
            <div className="flex items-center space-x-2">
              <Badge variant={mockData.metrics.materialsRecovered.trend === 'up' ? 'default' : 'destructive'}>
                {mockData.metrics.materialsRecovered.trend === 'up' ? (
                  <ArrowUpRight className="w-4 h-4" />
                ) : (
                  <ArrowDownRight className="w-4 h-4" />
                )}
                {mockData.metrics.materialsRecovered.change}%
              </Badge>
              <span className="text-sm text-muted-foreground">vs. last period</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Carbon Offset (tons)</CardTitle>
            <Leaf className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{mockData.metrics.carbonOffset.value}</div>
            <div className="flex items-center space-x-2">
              <Badge variant={mockData.metrics.carbonOffset.trend === 'up' ? 'default' : 'destructive'}>
                {mockData.metrics.carbonOffset.trend === 'up' ? (
                  <ArrowUpRight className="w-4 h-4" />
                ) : (
                  <ArrowDownRight className="w-4 h-4" />
                )}
                {mockData.metrics.carbonOffset.change}%
              </Badge>
              <span className="text-sm text-muted-foreground">vs. last period</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Programs</CardTitle>
            <Leaf className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{mockData.metrics.activePrograms.value}</div>
            <div className="flex items-center space-x-2">
              <Badge variant={mockData.metrics.activePrograms.trend === 'up' ? 'default' : 'destructive'}>
                {mockData.metrics.activePrograms.trend === 'up' ? (
                  <ArrowUpRight className="w-4 h-4" />
                ) : (
                  <ArrowDownRight className="w-4 h-4" />
                )}
                +{mockData.metrics.activePrograms.change}
              </Badge>
              <span className="text-sm text-muted-foreground">vs. last period</span>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Revenue Over Time</CardTitle>
          </CardHeader>
          <CardContent>
            <DashboardChart
              type="line"
              title="Monthly Revenue"
              data={mockData.revenueChart}
              dataKey="value"
              xAxisKey="date"
              height={300}
              colors={['#10B981']}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Materials Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <DashboardChart
              type="pie"
              title="Materials Distribution"
              data={mockData.materialChart}
              dataKey="value"
              xAxisKey="material"
              height={300}
              colors={['#10B981', '#6366F1', '#F59E0B', '#3B82F6']}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
} 
